import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { REAL_BASE_URL } from "../api/axiosInstance";
import {
  DESIGN_TOKENS,
  applyBrandCssVars,
  resolveMediaUrl,
} from "../designSystem/tokens";
import { useThemeMode } from "./ThemeContext";

const BrandingContext = createContext({
  settings: null,
  loading: true,
  refresh: async () => {},
  companyName: "EzyPayInd",
  mediaUrl: () => "",
  formatDate: () => "",
});

const DEFAULTS = {
  companyName: "EzyPayInd",
  legalName: "JSS MANAGEMENT AND SUPPLY",
  primaryColor: DESIGN_TOKENS.defaults.primaryColor,
  secondaryColor: DESIGN_TOKENS.defaults.secondaryColor,
  accentColor: DESIGN_TOKENS.defaults.accentColor,
  dateFormat: "DD MMM YYYY",
  timeFormat: "12h",
  maintenanceEnabled: false,
  loginBannerEnabled: true,
  authorizedSignatoryName: "Authorized Signatory",
  websiteUrl: "https://ezypayind.com",
  notificationsEnabled: true,
  toastPosition: "top-right",
};

function pad(n) {
  return String(n).padStart(2, "0");
}

function formatWithSettings(inputDate, type, settings) {
  if (!inputDate) return "";
  const d = new Date(inputDate);
  if (isNaN(d)) return "Invalid Date";

  const day = pad(d.getDate());
  const monthNum = pad(d.getMonth() + 1);
  const year = d.getFullYear();
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const monthShort = months[d.getMonth()];
  const fmt = settings?.dateFormat || "DD MMM YYYY";

  let dateStr;
  switch (fmt) {
    case "DD-MM-YYYY":
      dateStr = `${day}-${monthNum}-${year}`;
      break;
    case "MM-DD-YYYY":
      dateStr = `${monthNum}-${day}-${year}`;
      break;
    case "YYYY-MM-DD":
      dateStr = `${year}-${monthNum}-${day}`;
      break;
    case "DD MMM YYYY":
    default:
      dateStr = `${day} ${monthShort} ${year}`;
  }

  const use24 = settings?.timeFormat === "24h";
  let hours = d.getHours();
  const minutes = pad(d.getMinutes());
  const seconds = pad(d.getSeconds());
  let timeStr;
  if (use24) {
    timeStr = `${pad(hours)}:${minutes}:${seconds}`;
  } else {
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    timeStr = `${pad(hours)}:${minutes}:${seconds} ${ampm}`;
  }

  switch (String(type || "full").toLowerCase()) {
    case "date":
      return dateStr;
    case "time":
      return timeStr;
    default:
      return `${dateStr} ${timeStr}`;
  }
}

export const BrandingProvider = ({ children, pollMs = 60000 }) => {
  const { mode } = useThemeMode();
  const [settings, setSettings] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${REAL_BASE_URL}/api/settings/public`, {
        timeout: 15000,
        headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
        params: { t: Date.now() },
      });
      const data = res?.data?.data || res?.data || {};
      const next = { ...DEFAULTS, ...data };
      setSettings((prev) =>
        JSON.stringify(prev) === JSON.stringify(next) ? prev : next
      );
    } catch {
      /* keep defaults / last good */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, pollMs);
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh, pollMs]);

  useEffect(() => {
    applyBrandCssVars(settings, mode);
    if (typeof window !== "undefined") {
      window.__APP_SETTINGS__ = settings;
    }
    if (settings?.companyName) {
      document.title = `${settings.companyName} Admin`;
    }
    if (settings?.faviconUrl) {
      let link = document.querySelector("link[rel*='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = resolveMediaUrl(REAL_BASE_URL, settings.faviconUrl);
    }
  }, [settings, mode]);

  const mediaUrl = useCallback(
    (path) => resolveMediaUrl(REAL_BASE_URL, path),
    []
  );

  const formatDate = useCallback(
    (date, type = "full") => formatWithSettings(date, type, settings),
    [settings]
  );

  const value = useMemo(
    () => ({
      settings,
      loading,
      refresh,
      companyName: settings.companyName || DEFAULTS.companyName,
      legalName: settings.legalName || DEFAULTS.legalName,
      mediaUrl,
      formatDate,
      maintenanceEnabled: Boolean(settings.maintenanceEnabled),
    }),
    [settings, loading, refresh, mediaUrl, formatDate]
  );

  return (
    <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>
  );
};

export const useBranding = () => useContext(BrandingContext);

export { formatWithSettings };
